import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import {
  Save,
  RotateCcw,
  DollarSign,
  Clock,
  Percent,
  Package,
  Building,
  Mail,
  Phone,
  Globe,
  Upload,
  ImageIcon,
  Trash2,
  Link2,
  Unlink,
  CheckCircle2,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Plus,
  Pencil,
  FileText,
  ChevronDown,
  ChevronUp,
  Palette,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RichTextEditor from "@/components/RichTextEditor";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Settings navigation tabs
const SETTINGS_TABS = [
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "branding", label: "Branding", icon: Palette },
  { id: "integrations", label: "Integrations", icon: Zap },
  { id: "email", label: "Email", icon: Mail },
];

const SETTINGS_CONFIG = [
  {
    key: "labour_rate",
    label: "Labour Rate",
    description: "Hourly rate for production labour",
    icon: DollarSign,
    suffix: "/hr",
    type: "number",
    step: 0.01,
  },
  {
    key: "garments_per_hour",
    label: "Garments Per Hour",
    description: "Average garments processed per hour",
    icon: Clock,
    suffix: "garments",
    type: "number",
    step: 1,
  },
  {
    key: "margin_percent",
    label: "Default Margin",
    description: "Default margin percentage for new jobs",
    icon: Percent,
    suffix: "%",
    type: "percent",
    step: 0.01,
  },
  {
    key: "minimum_production_hours",
    label: "Minimum Production Hours",
    description: "Minimum hours charged for any job",
    icon: Clock,
    suffix: "hrs",
    type: "number",
    step: 0.1,
  },
  {
    key: "artwork_group_time_multiplier",
    label: "Print Group Time Multiplier",
    description: "Additional hours per artwork group",
    icon: Package,
    suffix: "hrs",
    type: "number",
    step: 0.01,
  },
  {
    key: "gst_rate",
    label: "GST Rate",
    description: "Goods and Services Tax rate",
    icon: Percent,
    suffix: "%",
    type: "percent",
    step: 0.01,
  },
  {
    key: "small_order_threshold",
    label: "Small Order Threshold",
    description: "Orders under this quantity trigger surcharge",
    icon: Package,
    suffix: "units",
    type: "number",
    step: 1,
  },
  {
    key: "small_order_surcharge",
    label: "Small Order Surcharge",
    description: "Extra charge for small orders (0 = disabled)",
    icon: DollarSign,
    suffix: "",
    type: "number",
    step: 1,
  },
  {
    key: "default_lead_time_days",
    label: "Default Production Lead Time",
    description: "Standard production lead time in days",
    icon: Clock,
    suffix: "days",
    type: "number",
    step: 1,
  },
];

export default function Settings() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("pricing");
  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Branding state
  const [branding, setBranding] = useState({ header_logo: null, favicon: null });
  const [isUploadingHeaderLogo, setIsUploadingHeaderLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const headerLogoInputRef = useRef(null);
  const faviconInputRef = useRef(null);
  
  // Xero integration state
  const [xeroStatus, setXeroStatus] = useState({ connected: false, tenant_name: "", has_credentials: false });
  const [xeroClientId, setXeroClientId] = useState("");
  const [xeroClientSecret, setXeroClientSecret] = useState("");
  const [showXeroSecret, setShowXeroSecret] = useState(false);
  const [isConnectingXero, setIsConnectingXero] = useState(false);
  const [isDisconnectingXero, setIsDisconnectingXero] = useState(false);
  const [isSavingXeroCredentials, setIsSavingXeroCredentials] = useState(false);

  // Google/Gmail integration state
  const [googleStatus, setGoogleStatus] = useState({ connected: false, email: "", has_credentials: false });
  const [gmailAccounts, setGmailAccounts] = useState([]); // All configured Gmail accounts with status
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isDisconnectingGoogle, setIsDisconnectingGoogle] = useState(false);
  const [isSavingGoogleCredentials, setIsSavingGoogleCredentials] = useState(false);

  // Email signatures state
  const [emailSignatures, setEmailSignatures] = useState({});
  const [signatureAccounts, setSignatureAccounts] = useState([]);
  const [isSavingSignatures, setIsSavingSignatures] = useState(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatePlaceholders, setTemplatePlaceholders] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);

  // Generate redirect URIs based on current domain (works across all environments)
  const xeroRedirectUri = `${window.location.origin}/api/xero/callback`;
  const googleRedirectUri = `${window.location.origin}/api/google/callback`;

  // Load data on mount and when search params change (OAuth callback)
  useEffect(() => {
    loadSettings();
    loadXeroStatus();
    loadGoogleStatus();
    loadBranding();
    loadEmailSignatures();
    loadEmailTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Handle OAuth callback params
  useEffect(() => {
    const xeroParam = searchParams.get("xero");
    if (xeroParam === "connected") {
      toast.success("Successfully connected to Xero!");
      loadXeroStatus();
    }
    
    const googleParam = searchParams.get("google");
    if (googleParam === "connected") {
      toast.success("Successfully connected to Gmail!");
      loadGoogleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadBranding = async () => {
    try {
      const res = await axios.get(`${API}/branding`);
      setBranding(res.data);
      
      // Apply favicon dynamically if set
      if (res.data.favicon) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement("link");
        link.rel = "icon";
        link.href = res.data.favicon;
        document.head.appendChild(link);
      }
    } catch (error) {
      console.error("Failed to load branding", error);
    }
  };

  const handleUploadHeaderLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/png", "image/svg+xml", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload PNG, SVG, JPEG, or WebP file");
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be under 2MB");
      return;
    }
    
    setIsUploadingHeaderLogo(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/branding/header-logo`, formData);
      toast.success("Header logo uploaded successfully");
      loadBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setIsUploadingHeaderLogo(false);
    }
  };

  const handleUploadFavicon = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/png", "image/x-icon", "image/vnd.microsoft.icon"];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith(".ico")) {
      toast.error("Please upload PNG or ICO file");
      return;
    }
    
    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error("Favicon must be under 500KB");
      return;
    }
    
    setIsUploadingFavicon(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/branding/favicon`, formData);
      toast.success("Favicon uploaded successfully");
      loadBranding();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload favicon");
    } finally {
      setIsUploadingFavicon(false);
    }
  };

  const handleRemoveHeaderLogo = async () => {
    try {
      await axios.delete(`${API}/branding/header-logo`);
      toast.success("Header logo removed");
      loadBranding();
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const handleRemoveFavicon = async () => {
    try {
      await axios.delete(`${API}/branding/favicon`);
      toast.success("Favicon removed");
      loadBranding();
      // Reset to default favicon
      const link = document.querySelector("link[rel~='icon']");
      if (link) link.href = "/favicon.ico";
    } catch (error) {
      toast.error("Failed to remove favicon");
    }
  };

  const loadXeroStatus = async () => {
    try {
      const res = await axios.get(`${API}/xero/status`);
      setXeroStatus(res.data);
    } catch (error) {
      console.error("Failed to load Xero status", error);
    }
  };

  const loadGoogleStatus = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/google/status`, { headers });
      setGoogleStatus(res.data);
      
      // Also load all Gmail accounts with their connection status
      const accountsRes = await axios.get(`${API}/gmail/accounts`, { headers });
      setGmailAccounts(accountsRes.data.accounts || []);
    } catch (error) {
      console.error("Failed to load Google status", error);
    }
  };

  const loadEmailSignatures = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-signatures`);
      setEmailSignatures(res.data.signatures || {});
      setSignatureAccounts(res.data.accounts || []);
    } catch (error) {
      console.error("Failed to load email signatures", error);
    }
  };

  const saveEmailSignatures = async () => {
    setIsSavingSignatures(true);
    try {
      await axios.put(`${API}/settings/email-signatures`, emailSignatures);
      toast.success("Email signatures saved successfully");
    } catch (error) {
      toast.error("Failed to save email signatures");
      console.error(error);
    } finally {
      setIsSavingSignatures(false);
    }
  };

  const updateSignature = (email, value) => {
    setEmailSignatures(prev => ({
      ...prev,
      [email]: value
    }));
  };

  // Email Templates Functions
  const loadEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API}/settings/email-templates`);
      const templates = res.data.templates || [];
      // Sort by sort_order
      templates.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setEmailTemplates(templates);
      setTemplatePlaceholders(res.data.placeholders || []);
    } catch (error) {
      console.error("Failed to load email templates", error);
    }
  };

  const handleSaveTemplate = async (template) => {
    setIsSavingTemplate(true);
    try {
      if (template.id) {
        // Update existing
        await axios.put(`${API}/settings/email-templates/${template.id}`, template);
        toast.success("Template updated");
      } else {
        // Create new
        await axios.post(`${API}/settings/email-templates`, template);
        toast.success("Template created");
      }
      await loadEmailTemplates();
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
      console.error(error);
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/settings/email-templates/${templateId}`);
      toast.success("Template deleted");
      await loadEmailTemplates();
    } catch (error) {
      toast.error("Failed to delete template");
      console.error(error);
    }
    setDeleteTemplateId(null);
  };

  const handleToggleTemplate = async (template) => {
    try {
      await axios.put(`${API}/settings/email-templates/${template.id}`, {
        enabled: !template.enabled
      });
      await loadEmailTemplates();
      toast.success(`Template ${template.enabled ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error("Failed to update template");
      console.error(error);
    }
  };

  const handleResetTemplates = async () => {
    try {
      await axios.post(`${API}/settings/email-templates/reset`);
      await loadEmailTemplates();
      toast.success("Templates reset to defaults");
    } catch (error) {
      toast.error("Failed to reset templates");
      console.error(error);
    }
  };

  const openNewTemplateModal = () => {
    setEditingTemplate({
      name: "",
      subject: "",
      body: "",
      category: "general",
      enabled: true,
      sort_order: emailTemplates.length
    });
    setShowTemplateModal(true);
  };

  const openEditTemplateModal = (template) => {
    setEditingTemplate({ ...template });
    setShowTemplateModal(true);
  };

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      setOriginalSettings(res.data);
    } catch (error) {
      toast.error("Failed to load settings");
      console.error(error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await axios.put(`${API}/settings`, settings);
      setSettings(res.data);
      setOriginalSettings(res.data);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      const res = await axios.post(`${API}/settings/reset`);
      setSettings(res.data);
      setOriginalSettings(res.data);
      toast.success("Settings reset to defaults");
    } catch (error) {
      toast.error("Failed to reset settings");
      console.error(error);
    } finally {
      setIsResetting(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/settings/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo uploaded successfully");
      // Reload settings to get the new logo
      await loadSettings();
    } catch (error) {
      toast.error("Failed to upload logo");
      console.error(error);
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = () => {
    setSettings({ ...settings, business_logo_url: "" });
  };

  // Xero integration functions
  const saveXeroCredentials = async () => {
    if (!xeroClientId || !xeroClientSecret) {
      toast.error("Please enter both Client ID and Client Secret");
      return;
    }
    
    setIsSavingXeroCredentials(true);
    try {
      await axios.post(`${API}/xero/credentials`, {
        client_id: xeroClientId,
        client_secret: xeroClientSecret,
      });
      
      // Also save the redirect URI to settings
      await axios.put(`${API}/settings`, {
        xero_client_id: xeroClientId,
        xero_client_secret: xeroClientSecret,
        xero_redirect_uri: xeroRedirectUri,
      });
      
      toast.success("Xero credentials saved");
      setXeroStatus(prev => ({ ...prev, has_credentials: true }));
      await loadSettings();
    } catch (error) {
      toast.error("Failed to save Xero credentials");
      console.error(error);
    } finally {
      setIsSavingXeroCredentials(false);
    }
  };

  const connectToXero = async () => {
    setIsConnectingXero(true);
    try {
      const res = await axios.get(`${API}/xero/auth`);
      // Redirect to Xero authorization
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Xero connection");
      console.error(error);
      setIsConnectingXero(false);
    }
  };

  const disconnectXero = async () => {
    setIsDisconnectingXero(true);
    try {
      await axios.post(`${API}/xero/disconnect`);
      toast.success("Disconnected from Xero");
      setXeroStatus({ connected: false, tenant_name: "", has_credentials: true });
    } catch (error) {
      toast.error("Failed to disconnect from Xero");
      console.error(error);
    } finally {
      setIsDisconnectingXero(false);
    }
  };

  const copyRedirectUri = () => {
    navigator.clipboard.writeText(xeroRedirectUri);
    toast.success("Redirect URI copied to clipboard");
  };

  // Google/Gmail integration functions
  const saveGoogleCredentials = async () => {
    if (!googleClientId || !googleClientSecret) {
      toast.error("Please enter both Client ID and Client Secret");
      return;
    }
    
    setIsSavingGoogleCredentials(true);
    try {
      await axios.post(`${API}/google/credentials`, {
        client_id: googleClientId,
        client_secret: googleClientSecret,
      });
      
      // Also save the redirect URI to settings
      await axios.put(`${API}/settings`, {
        google_client_id: googleClientId,
        google_client_secret: googleClientSecret,
        google_redirect_uri: googleRedirectUri,
      });
      
      toast.success("Google credentials saved");
      setGoogleStatus(prev => ({ ...prev, has_credentials: true }));
      await loadSettings();
    } catch (error) {
      toast.error("Failed to save Google credentials");
      console.error(error);
    } finally {
      setIsSavingGoogleCredentials(false);
    }
  };

  const connectToGoogle = async () => {
    setIsConnectingGoogle(true);
    try {
      const res = await axios.get(`${API}/google/auth`);
      // Redirect to Google authorization
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Google connection");
      console.error(error);
      setIsConnectingGoogle(false);
    }
  };

  // Connect a specific Gmail account
  const connectGmailAccount = async (accountEmail) => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/authorize?account_email=${encodeURIComponent(accountEmail)}`);
      // Redirect to Google authorization
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start Gmail connection");
      console.error(error);
    }
  };

  const disconnectGoogle = async () => {
    setIsDisconnectingGoogle(true);
    try {
      await axios.post(`${API}/google/disconnect`);
      toast.success("Disconnected from Gmail");
      setGoogleStatus({ connected: false, email: "", has_credentials: true });
    } catch (error) {
      toast.error("Failed to disconnect from Google");
      console.error(error);
    } finally {
      setIsDisconnectingGoogle(false);
    }
  };

  const copyGoogleRedirectUri = () => {
    navigator.clipboard.writeText(googleRedirectUri);
    toast.success("Google Redirect URI copied to clipboard");
  };

  const updateSetting = (key, value, type) => {
    if (type === "text") {
      setSettings({ ...settings, [key]: value });
      return;
    }
    let parsedValue = parseFloat(value) || 0;
    if (type === "percent") {
      parsedValue = parsedValue / 100;
    }
    setSettings({ ...settings, [key]: parsedValue });
  };

  const getDisplayValue = (key, type) => {
    if (!settings) return "";
    const value = settings[key];
    if (type === "percent") {
      return (value * 100).toFixed(0);
    }
    return value;
  };

  const hasChanges = settings && originalSettings && 
    JSON.stringify(settings) !== JSON.stringify(originalSettings);

  if (!settings) {
    return (
      <WorkspaceLayout title="Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-400">Loading settings...</div>
        </div>
      </WorkspaceLayout>
    );
  }

  // TopBar actions
  const topBarActions = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleReset}
        disabled={isResetting}
        data-testid="reset-settings-btn"
      >
        <RotateCcw className={`w-4 h-4 mr-1.5 ${isResetting ? "animate-spin" : ""}`} />
        Reset
      </Button>
      <Button
        size="sm"
        onClick={handleSave}
        disabled={isSaving || !hasChanges}
        data-testid="save-settings-btn"
        className={hasChanges ? "bg-blue-600 hover:bg-blue-700" : ""}
      >
        <Save className="w-4 h-4 mr-1.5" />
        {isSaving ? "Saving..." : hasChanges ? "Save*" : "Save"}
      </Button>
    </div>
  );

  return (
    <WorkspaceLayout title="Settings" actions={topBarActions}>
      <div className="flex h-[calc(100vh-56px)]">
        {/* Left Column - Settings Navigation */}
        <div className="w-56 shrink-0 border-r border-gray-200 bg-gray-50/50 p-4">
          <nav className="space-y-1">
            {SETTINGS_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive 
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200" 
                      : "text-gray-600 hover:bg-white hover:text-gray-900"
                  }`}
                  data-testid={`settings-tab-${tab.id}`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? "text-blue-600" : "text-gray-400"}`} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
          
          {/* Info Cards at bottom */}
          <div className="mt-8 space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs font-medium text-blue-800 mb-1">Labour Calc</p>
              <p className="text-[10px] text-blue-600 leading-relaxed">
                Hours = MAX(Qty / Rate, Min) + (Groups × Mult)
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-100">
              <p className="text-xs font-medium text-green-800 mb-1">Margin</p>
              <p className="text-[10px] text-green-600">
                Sell = Cost / (1 - Margin%)
              </p>
            </div>
          </div>
        </div>

        {/* Right Column - Settings Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {/* Pricing Tab */}
          {activeTab === "pricing" && (
            <div className="space-y-6 max-w-3xl">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Global Parameters</h2>
                <p className="text-sm text-gray-500 mt-1">
                  These values affect all quote calculations across the system.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SETTINGS_CONFIG.map((config) => {
                  const Icon = config.icon;
                  return (
                    <div
                      key={config.key}
                      className="p-4 rounded-lg border border-gray-100 bg-gray-50/50"
                      data-testid={`setting-${config.key}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-gray-400" />
                        <Label htmlFor={config.key} className="text-sm font-medium text-gray-700">
                          {config.label}
                        </Label>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        {config.description}
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          id={config.key}
                          type="number"
                          step={config.step}
                          value={getDisplayValue(config.key, config.type)}
                          onChange={(e) =>
                            updateSetting(config.key, e.target.value, config.type)
                          }
                          data-testid={`input-${config.key}`}
                          className="font-mono max-w-[120px] h-9 text-sm border-gray-200"
                        />
                        <span className="text-xs text-gray-500">
                          {config.suffix}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === "branding" && (
            <div className="space-y-8 max-w-3xl">
              {/* PDF Branding Section */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Building className="w-5 h-5 text-gray-400" />
                    PDF Branding
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    These details appear in the header of exported PDF quotes.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Logo Upload */}
                  <div className="md:col-span-2 p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-4 h-4 text-gray-400" />
                      <Label className="text-sm font-medium text-gray-700">Business Logo</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload a logo for PDF header (PNG or JPG, max 1MB)
                    </p>
                    <div className="flex items-center gap-4">
                      {settings?.business_logo_url ? (
                        <div className="flex items-center gap-4">
                          <div className="h-14 w-28 border rounded bg-white flex items-center justify-center overflow-hidden">
                            <img 
                              src={settings.business_logo_url} 
                              alt="Business Logo" 
                              className="max-h-12 max-w-24 object-contain"
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRemoveLogo}
                            className="text-red-600 hover:text-red-700 h-8 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            id="logo-upload"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={isUploadingLogo}
                            data-testid="upload-logo-btn"
                            className="h-8 text-xs"
                          >
                            <Upload className="w-3 h-3 mr-1.5" />
                            {isUploadingLogo ? "Uploading..." : "Upload Logo"}
                          </Button>
                          <span className="text-xs text-gray-400">No logo uploaded</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Business Name */}
                  <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Building className="w-4 h-4 text-gray-400" />
                      <Label htmlFor="business_name" className="text-sm font-medium text-gray-700">Business Name</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Appears in PDF header</p>
                    <Input
                      id="business_name"
                      value={settings?.business_name || ""}
                      onChange={(e) => updateSetting("business_name", e.target.value, "text")}
                      placeholder="Alpine Armour"
                      data-testid="input-business_name"
                      className="h-9 text-sm border-gray-200"
                    />
                  </div>

                  {/* Business Email */}
                  <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <Label htmlFor="business_email" className="text-sm font-medium text-gray-700">Business Email</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Contact email for quotes</p>
                    <Input
                      id="business_email"
                      type="email"
                      value={settings?.business_email || ""}
                      onChange={(e) => updateSetting("business_email", e.target.value, "text")}
                      placeholder="quotes@alpinearmour.com.au"
                      data-testid="input-business_email"
                      className="h-9 text-sm border-gray-200"
                    />
                  </div>

                  {/* Business Phone */}
                  <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <Label htmlFor="business_phone" className="text-sm font-medium text-gray-700">Business Phone</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Contact phone for quotes</p>
                    <Input
                      id="business_phone"
                      value={settings?.business_phone || ""}
                      onChange={(e) => updateSetting("business_phone", e.target.value, "text")}
                      placeholder="0400 000 000"
                      data-testid="input-business_phone"
                      className="h-9 text-sm border-gray-200"
                    />
                  </div>

                  {/* Business Website */}
                  <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <Label htmlFor="business_website" className="text-sm font-medium text-gray-700">Business Website</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">Company website URL</p>
                    <Input
                      id="business_website"
                      value={settings?.business_website || ""}
                      onChange={(e) => updateSetting("business_website", e.target.value, "text")}
                      placeholder="www.alpinearmour.com.au"
                      data-testid="input-business_website"
                      className="h-9 text-sm border-gray-200"
                    />
                  </div>
                </div>
              </div>

              {/* App Branding Section */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-gray-400" />
                    App Branding
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload branding assets for the header, login page, and browser tab.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Header / Login Logo */}
                  <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-4 h-4 text-gray-400" />
                      <Label className="text-sm font-medium text-gray-700">Header / Login Logo</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      SVG or PNG, max 2MB
                    </p>
                    <div className="flex items-center gap-4">
                      {branding.header_logo ? (
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-24 border rounded bg-white flex items-center justify-center overflow-hidden">
                            <img 
                              src={branding.header_logo} 
                              alt="Header Logo" 
                              className="max-h-8 max-w-20 object-contain"
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/branding/header-logo`);
                                toast.success("Header logo removed");
                                loadBranding();
                              } catch (error) {
                                toast.error("Failed to remove logo");
                              }
                            }}
                            className="text-red-600 hover:text-red-700 h-8 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            ref={headerLogoInputRef}
                            type="file"
                            accept="image/png,image/svg+xml,image/jpeg,image/webp"
                            onChange={handleUploadHeaderLogo}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => headerLogoInputRef.current?.click()}
                            disabled={isUploadingHeaderLogo}
                            className="h-8 text-xs"
                          >
                            <Upload className="w-3 h-3 mr-1.5" />
                            {isUploadingHeaderLogo ? "Uploading..." : "Upload"}
                          </Button>
                          <span className="text-xs text-gray-400">No logo</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Favicon */}
                  <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-4 h-4 text-gray-400" />
                      <Label className="text-sm font-medium text-gray-700">Favicon</Label>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      PNG or ICO, max 500KB
                    </p>
                    <div className="flex items-center gap-4">
                      {branding.favicon ? (
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 border rounded bg-white flex items-center justify-center overflow-hidden">
                            <img 
                              src={branding.favicon} 
                              alt="Favicon" 
                              className="max-h-6 max-w-6 object-contain"
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.delete(`${API}/branding/favicon`);
                                toast.success("Favicon removed");
                                loadBranding();
                              } catch (error) {
                                toast.error("Failed to remove favicon");
                              }
                            }}
                            className="text-red-600 hover:text-red-700 h-8 text-xs"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <input
                            ref={faviconInputRef}
                            type="file"
                            accept="image/png,image/x-icon,image/vnd.microsoft.icon,.ico"
                            onChange={handleUploadFavicon}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => faviconInputRef.current?.click()}
                            disabled={isUploadingFavicon}
                            className="h-8 text-xs"
                          >
                            <Upload className="w-3 h-3 mr-1.5" />
                            {isUploadingFavicon ? "Uploading..." : "Upload"}
                          </Button>
                          <span className="text-xs text-gray-400">No favicon</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Public App URL Section */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-gray-400" />
                    Public App URL
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    The base URL used for client-facing links in emails.
                  </p>
                </div>
                
                <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <Label htmlFor="public_app_url" className="text-sm font-medium text-gray-700">Public App URL</Label>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Leave blank to auto-detect from browser.
                  </p>
                  <Input
                    id="public_app_url"
                    value={settings?.public_app_url || ""}
                    onChange={(e) => updateSetting("public_app_url", e.target.value, "text")}
                    placeholder={window.location.origin}
                    data-testid="input-public_app_url"
                    className="h-9 text-sm border-gray-200"
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Current: {settings?.public_app_url || window.location.origin}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === "integrations" && (
            <div className="space-y-8 max-w-3xl">
              {/* Xero Integration */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#13B5EA]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-.5 17.5l-5-5 1.5-1.5 3.5 3.5 6.5-6.5 1.5 1.5-8 8z"/>
                    </svg>
                    Xero Integration
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Connect to Xero to create draft invoices directly from approved quotes.
                  </p>
                </div>
                
                {/* Connection Status */}
                <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${xeroStatus.connected ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className="font-medium text-sm">
                        {xeroStatus.connected ? 'Connected' : 'Not Connected'}
                      </span>
                      {xeroStatus.connected && xeroStatus.tenant_name && (
                        <span className="text-sm text-gray-500">
                          to <strong>{xeroStatus.tenant_name}</strong>
                        </span>
                      )}
                    </div>
                    {xeroStatus.connected && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={disconnectXero}
                        disabled={isDisconnectingXero}
                        className="text-red-600 hover:text-red-700 h-8 text-xs"
                        data-testid="xero-disconnect-btn"
                      >
                        <Unlink className="w-3 h-3 mr-1.5" />
                        {isDisconnectingXero ? "Disconnecting..." : "Disconnect"}
                      </Button>
                    )}
                  </div>
                </div>

                {!xeroStatus.connected && (
                  <div className="space-y-4">
                    {/* Redirect URI */}
                    <div className="p-4 rounded-lg border border-blue-100 bg-blue-50/50">
                      <div className="flex items-center gap-2 mb-1">
                        <Link2 className="w-4 h-4 text-blue-600" />
                        <Label className="text-sm font-medium text-blue-900">Redirect URI</Label>
                      </div>
                      <p className="text-xs text-blue-700 mb-2">
                        Add this to your Xero App at{" "}
                        <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer" className="underline">
                          developer.xero.com
                        </a>
                      </p>
                      <div className="flex items-center gap-2">
                        <Input value={xeroRedirectUri} readOnly className="font-mono text-xs bg-white h-9 border-blue-200" />
                        <Button variant="outline" size="sm" onClick={copyRedirectUri} className="h-9 px-3">
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Credentials */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                        <Label htmlFor="xero_client_id" className="text-sm font-medium text-gray-700">Client ID</Label>
                        <Input
                          id="xero_client_id"
                          value={xeroClientId}
                          onChange={(e) => setXeroClientId(e.target.value)}
                          placeholder="Xero Client ID"
                          className="mt-2 h-9 text-sm border-gray-200"
                          data-testid="xero-client-id"
                        />
                      </div>
                      <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                        <Label htmlFor="xero_client_secret" className="text-sm font-medium text-gray-700">Client Secret</Label>
                        <div className="relative mt-2">
                          <Input
                            id="xero_client_secret"
                            type={showXeroSecret ? "text" : "password"}
                            value={xeroClientSecret}
                            onChange={(e) => setXeroClientSecret(e.target.value)}
                            placeholder="Xero Client Secret"
                            className="h-9 text-sm pr-10 border-gray-200"
                            data-testid="xero-client-secret"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-9 px-3"
                            onClick={() => setShowXeroSecret(!showXeroSecret)}
                          >
                            {showXeroSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={saveXeroCredentials}
                        disabled={isSavingXeroCredentials || !xeroClientId || !xeroClientSecret}
                        className="h-9 text-xs"
                        data-testid="xero-save-credentials-btn"
                      >
                        <Save className="w-3 h-3 mr-1.5" />
                        {isSavingXeroCredentials ? "Saving..." : "Save Credentials"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={connectToXero}
                        disabled={isConnectingXero || !xeroStatus.has_credentials}
                        className="h-9 text-xs bg-[#13B5EA] hover:bg-[#0fa3d4]"
                        data-testid="xero-connect-btn"
                      >
                        <Link2 className="w-3 h-3 mr-1.5" />
                        {isConnectingXero ? "Connecting..." : "Connect to Xero"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Google/Gmail Integration */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-red-500" />
                    Google / Gmail Integration
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Connect your Gmail accounts to send and receive emails within the app.
                  </p>
                </div>
                
                {/* All Email Accounts Status */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Email Accounts</Label>
                  {gmailAccounts.length > 0 ? (
                    <div className="space-y-2">
                      {gmailAccounts.map((account) => (
                        <div key={account.email} className="p-3 rounded-lg border border-gray-100 bg-gray-50/50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-2.5 h-2.5 rounded-full ${account.connected ? 'bg-green-500' : 'bg-amber-400'}`}></div>
                            <div>
                              <span className="font-medium text-sm text-gray-900">{account.email}</span>
                              <span className={`ml-2 text-xs ${account.connected ? 'text-green-600' : 'text-amber-600'}`}>
                                {account.connected ? 'Connected' : 'Not Connected'}
                              </span>
                            </div>
                          </div>
                          {!account.connected && googleStatus.has_credentials && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => connectGmailAccount(account.email)}
                              className="h-7 text-xs"
                            >
                              <Link2 className="w-3 h-3 mr-1" />
                              Connect
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${googleStatus.connected ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className="font-medium text-sm">
                          {googleStatus.connected ? 'Connected' : 'Not Connected'}
                        </span>
                        {googleStatus.connected && googleStatus.email && (
                          <span className="text-sm text-gray-500">
                            as <strong>{googleStatus.email}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {googleStatus.connected && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={disconnectGoogle}
                      disabled={isDisconnectingGoogle}
                      className="text-red-600 hover:text-red-700 h-8 text-xs"
                      data-testid="google-disconnect-btn"
                    >
                      <Unlink className="w-3 h-3 mr-1.5" />
                      {isDisconnectingGoogle ? "Disconnecting..." : "Disconnect All"}
                    </Button>
                  </div>
                )}

                {!googleStatus.connected && (
                  <div className="space-y-4">
                    {/* Redirect URI */}
                    <div className="p-4 rounded-lg border border-blue-100 bg-blue-50/50">
                      <div className="flex items-center gap-2 mb-1">
                        <Link2 className="w-4 h-4 text-blue-600" />
                        <Label className="text-sm font-medium text-blue-900">Redirect URI</Label>
                      </div>
                      <p className="text-xs text-blue-700 mb-2">
                        Add this to your Google Cloud Console OAuth 2.0 credentials
                      </p>
                      <div className="flex items-center gap-2">
                        <Input value={googleRedirectUri} readOnly className="font-mono text-xs bg-white h-9 border-blue-200" />
                        <Button variant="outline" size="sm" onClick={copyGoogleRedirectUri} className="h-9 px-3">
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Credentials */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                        <Label htmlFor="google_client_id" className="text-sm font-medium text-gray-700">Client ID</Label>
                        <Input
                          id="google_client_id"
                          value={googleClientId}
                          onChange={(e) => setGoogleClientId(e.target.value)}
                          placeholder="Google Client ID"
                          className="mt-2 h-9 text-sm border-gray-200"
                          data-testid="google-client-id"
                        />
                      </div>
                      <div className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                        <Label htmlFor="google_client_secret" className="text-sm font-medium text-gray-700">Client Secret</Label>
                        <div className="relative mt-2">
                          <Input
                            id="google_client_secret"
                            type={showGoogleSecret ? "text" : "password"}
                            value={googleClientSecret}
                            onChange={(e) => setGoogleClientSecret(e.target.value)}
                            placeholder="Google Client Secret"
                            className="h-9 text-sm pr-10 border-gray-200"
                            data-testid="google-client-secret"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-9 px-3"
                            onClick={() => setShowGoogleSecret(!showGoogleSecret)}
                          >
                            {showGoogleSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={saveGoogleCredentials}
                        disabled={isSavingGoogleCredentials || !googleClientId || !googleClientSecret}
                        className="h-9 text-xs"
                        data-testid="google-save-credentials-btn"
                      >
                        <Save className="w-3 h-3 mr-1.5" />
                        {isSavingGoogleCredentials ? "Saving..." : "Save Credentials"}
                      </Button>
                      <Button
                        size="sm"
                        onClick={connectToGoogle}
                        disabled={isConnectingGoogle || !googleStatus.has_credentials}
                        className="h-9 text-xs bg-red-500 hover:bg-red-600"
                        data-testid="google-connect-btn"
                      >
                        <Link2 className="w-3 h-3 mr-1.5" />
                        {isConnectingGoogle ? "Connecting..." : "Connect to Google"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === "email" && (
            <div className="space-y-8 max-w-4xl">
              {/* Email Signatures Section */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-gray-400" />
                    Email Signatures
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Customize email signatures for each sending account.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {signatureAccounts.length === 0 ? (
                    <div className="p-6 rounded-lg border border-gray-100 bg-gray-50/50 text-center">
                      <Mail className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No email accounts connected</p>
                      <p className="text-xs text-gray-400 mt-1">Connect Google/Gmail to add signatures</p>
                    </div>
                  ) : (
                    signatureAccounts.map((email) => (
                      <div key={email} className="p-4 rounded-lg border border-gray-100 bg-gray-50/50">
                        <div className="flex items-center gap-2 mb-3">
                          <Label className="font-medium text-gray-900">{email}</Label>
                        </div>
                        <RichTextEditor
                          value={emailSignatures[email] || ""}
                          onChange={(value) => {
                            setEmailSignatures(prev => ({ ...prev, [email]: value }));
                          }}
                          placeholder="Enter your email signature..."
                          className="min-h-[100px] bg-white border-gray-200"
                        />
                      </div>
                    ))
                  )}
                  
                  {signatureAccounts.length > 0 && (
                    <Button
                      onClick={saveEmailSignatures}
                      disabled={isSavingSignatures}
                      size="sm"
                      className="h-9 text-xs"
                      data-testid="save-signatures-btn"
                    >
                      <Save className="w-3 h-3 mr-1.5" />
                      {isSavingSignatures ? "Saving..." : "Save Signatures"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Email Templates Section */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-gray-400" />
                      Email Templates
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Create and manage email templates for common communications.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetTemplates}
                      className="h-8 text-xs"
                    >
                      <RotateCcw className="w-3 h-3 mr-1.5" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={openNewTemplateModal}
                      className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-3 h-3 mr-1.5" />
                      New Template
                    </Button>
                  </div>
                </div>

                {/* Placeholder Reference */}
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <h4 className="text-xs font-medium text-blue-800 mb-2">Available Placeholders</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {templatePlaceholders.map(p => (
                      <Badge key={p} variant="secondary" className="font-mono text-[10px] bg-white text-blue-700">
                        {`{{${p}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Templates List */}
                <div className="space-y-2">
                  {emailTemplates.length === 0 ? (
                    <div className="p-6 rounded-lg border border-gray-100 bg-gray-50/50 text-center">
                      <FileText className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">No templates yet</p>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={openNewTemplateModal}
                        className="text-blue-600 mt-1"
                      >
                        Create your first template
                      </Button>
                    </div>
                  ) : (
                    emailTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="rounded-lg border border-gray-100 bg-gray-50/50 overflow-hidden"
                        data-testid={`template-${template.id}`}
                      >
                        <div
                          className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-100/50"
                          onClick={() => setExpandedTemplateId(expandedTemplateId === template.id ? null : template.id)}
                        >
                          <div className="flex items-center gap-3">
                            {expandedTemplateId === template.id ? (
                              <ChevronUp className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="font-medium text-sm text-gray-900">{template.name}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {template.type || 'general'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); openEditTemplateModal(template); }}
                              className="h-7 px-2"
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); setDeleteTemplateId(template.id); }}
                              className="h-7 px-2 text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {expandedTemplateId === template.id && (
                          <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-white">
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="text-gray-500">Subject:</span>
                                <span className="ml-2 text-gray-900">{template.subject}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Body:</span>
                                <div 
                                  className="mt-1 p-3 bg-gray-50 rounded text-gray-700 text-xs"
                                  dangerouslySetInnerHTML={{ __html: template.body }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template Edit/Create Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate?.id ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Use placeholders like {"{{job_name}}"} for dynamic content.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template-name">Template Name</Label>
                <Input
                  id="template-name"
                  value={editingTemplate?.name || ""}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Quote Follow-up"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="template-type">Type</Label>
                <Select
                  value={editingTemplate?.type || "general"}
                  onValueChange={(value) => setEditingTemplate(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger id="template-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-subject">Subject Line</Label>
              <Input
                id="template-subject"
                value={editingTemplate?.subject || ""}
                onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="e.g., Quote for {{job_name}}"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Body</Label>
              <RichTextEditor
                value={editingTemplate?.body || ""}
                onChange={(value) => setEditingTemplate(prev => ({ ...prev, body: value }))}
                placeholder="Write your template content..."
                className="min-h-[200px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleSaveTemplate(editingTemplate)}
              disabled={isSavingTemplate || !editingTemplate?.name || !editingTemplate?.subject}
            >
              {isSavingTemplate ? "Saving..." : "Save Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Confirmation */}
      <AlertDialog open={!!deleteTemplateId} onOpenChange={() => setDeleteTemplateId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The template will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteTemplate(deleteTemplateId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WorkspaceLayout>
  );
}
